import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  FormControl,
  Divider,
  Paper,
  Link,
} from "@mui/material";
import { orderBy } from "lodash";

import { bottomButton } from "../../common/styles";
import {
  MEDICAL_ACCOUNT,
  MEDICAL_HISTORY,
  INSURANCE_INFORMATION,
} from "../../routes/path";
//共通コンポーネント
import { Header, Pulldown, MainColorButton, Loading } from "../../components";
import { getPrescriptionHistory } from "../../server/axios";
// redux
import store from "../../store";
import { closeWindow } from "../../utils/getLineProfile";

/**
 * ご利用履歴画面
 * @returns
 */
const PrescriptionHistory = () => {
  const navigate = useNavigate();
  // 選択された利用者
  const [user, setUser] = useState(0);
  // 選択された月
  const [month, setMonth] = useState("");
  // Loadingコンポネント表示非表示state
  const [loadingOpen, setLoadingOpen] = useState(false);
  // ご利用者名前選択肢
  const [userNames, setUserNames] = useState([]);
  // ご利用月選択肢
  const [months, setMonths] = useState([]);
  // ご利用履歴データ
  const [data, setData] = useState([]);
  // オリジナルデータ
  const [resData, setResData] = useState([]);
  // 初回render反応しないように
  const RENDER_TIME = process.env.NODE_ENV === "development" ? 1 : 0;
  const firstRenderRef = useRef(0);

  // family idでorder
  const getOrderedYearMonthArray = (array = [], index) => {
    return orderBy(
      array
        .find((r) => r.familyId === index)
        .dateInfo.map((r) => ({
          id: r.receiptedYearMonth,
          value: r.receiptedYearMonth,
          array: r.prescriptionInfo,
        })),
      ["id"],
      ["desc"]
    );
  };

  // 利用履歴年月でorder
  const getOrderedHistoryArray = (array = [], m) => {
    return orderBy(
      array.find((r) => r.id === m).array,
      ["receiptedDate"],
      ["desc"]
    );
  };

  // 初期化
  useEffect(() => {
    setLoadingOpen(true);
    const selectLineInfo = (state) => state.prescription.lineInfo;
    const lineInfo = selectLineInfo(store.getState());
    getPrescriptionHistory({ lineId: lineInfo.lineId }).then((res) => {
      if (res.data && res.data.length !== 0) {
        setResData(res.data);
        setUserNames(
          orderBy(
            res.data.map((r) => ({
              id: r.familyId,
              value: r.fullName,
            })),
            ["id"]
          )
        );
        setUser(0);
        const orderedYearMonthArray = getOrderedYearMonthArray(res.data, 0);
        setMonths(orderedYearMonthArray);
        setMonth(orderedYearMonthArray[0].id);
        const history = getOrderedHistoryArray(
          orderedYearMonthArray,
          orderedYearMonthArray[0].id
        );
        setData(history);
      }
      setLoadingOpen(false);
    });
  }, []);

  // ご利用者変更
  useEffect(() => {
    if (
      resData &&
      resData.length !== 0 &&
      firstRenderRef.current > RENDER_TIME
    ) {
      const orderedYearMonthArray = getOrderedYearMonthArray(resData, user);
      setMonths(orderedYearMonthArray);
      setMonth(orderedYearMonthArray[0].id);
      const history = getOrderedHistoryArray(
        orderedYearMonthArray,
        orderedYearMonthArray[0].id
      );
      setData(history);
    }
  }, [user]);

  // ご利用月変更
  useEffect(() => {
    if (
      resData &&
      resData.length !== 0 &&
      firstRenderRef.current > RENDER_TIME
    ) {
      const orderedYearMonthArray = getOrderedYearMonthArray(resData, user);
      const history = getOrderedHistoryArray(orderedYearMonthArray, month);
      setData(history);
    }
    firstRenderRef.current = firstRenderRef.current + 1;
  }, [month]);

  return (
    <>
      <Loading open={loadingOpen} />
      <Header title="ご利用履歴(お申込履歴)" />
      <Stack
        height="76vh"
        marginTop="9vh"
        marginLeft="auto"
        marginRight="auto"
        spacing="6%"
      >
        {!loadingOpen &&
          (userNames && userNames.length !== 0 && data && data.length !== 0 ? (
            <>
              <Stack spacing="2%" paddingLeft="3%">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box width="20%">
                    <Typography>
                      <strong>ご利用者</strong>
                    </Typography>
                  </Box>
                  <Box width="60%">
                    <FormControl fullWidth>
                      <Pulldown
                        size="small"
                        withLabel={false}
                        withMenuProps={true}
                        items={userNames}
                        value={user}
                        setValue={(p) => setUser(p)}
                      />
                    </FormControl>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box width="20%">
                    <Typography>
                      <strong>ご利用月</strong>
                    </Typography>
                  </Box>
                  <Box width="40%">
                    <FormControl fullWidth>
                      <Pulldown
                        size="small"
                        withLabel={false}
                        withMenuProps={true}
                        items={months}
                        value={month}
                        setValue={(p) => setMonth(p)}
                      />
                    </FormControl>
                  </Box>
                </Stack>
              </Stack>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ height: "60vh", overflowY: "scroll" }}
              >
                <Stack divider={<Divider />}>
                  {data.map((item, index) => (
                    <Stack
                      key={index}
                      spacing="2%"
                      sx={{ paddingX: "3%", paddingY: "1%" }}
                    >
                      <Stack direction="row" spacing="2%" alignItems="center">
                        <Box width="50%">
                          <Typography>
                            <strong>受付番号</strong>
                          </Typography>
                        </Box>
                        <Box width="50%">
                          <Typography>{item.receiptedNumber}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing="2%" alignItems="center">
                        <Box width="50%">
                          <Typography>
                            <strong>受付日時</strong>
                          </Typography>
                        </Box>
                        <Box width="50%">
                          <Typography>{item.receiptedDate}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing="2%" alignItems="center">
                        <Box width="50%">
                          <Typography>
                            <strong>ご利用の薬局</strong>
                          </Typography>
                        </Box>
                        <Box width="50%">
                          <Typography>{item.tenantName}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing="2%" alignItems="center">
                        <Box width="50%">
                          <Typography>
                            <strong>受付ステータス</strong>
                          </Typography>
                        </Box>
                        <Box width="50%">
                          {item.linkDisplay !== "" ? (
                            <Link
                              onTouchEnd={() => {
                                const {
                                  receiptedNumber,
                                  siteId,
                                  familyId,
                                  customerId,
                                } = item;
                                item.deliveryStatusDisplay === "会計へ" &&
                                  navigate(
                                    `${MEDICAL_ACCOUNT}?receiptedNumber=${receiptedNumber}&siteId=${siteId}&familyId=${familyId}&customerId=${customerId}`
                                  );

                                item.deliveryStatusDisplay === "再登録待" &&
                                  navigate(
                                    `${INSURANCE_INFORMATION}?receiptedNumber=${receiptedNumber}&message=false`,
                                    {
                                      state: {
                                        receiptedNumber: item.receiptedNumber,
                                      },
                                    }
                                  );
                              }}
                            >
                              {item.deliveryStatusDisplay}
                            </Link>
                          ) : (
                            <Typography>
                              {item.deliveryStatusDisplay}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </>
          ) : (
            <Box
              sx={{
                width: "100%",
                textAlign: "center",
                paddingTop: "5vh",
              }}
            >
              <Typography variant="h6">ご利用履歴ございません。</Typography>
            </Box>
          ))}
      </Stack>
      <MainColorButton
        label="閉じる"
        onClick={() => closeWindow()}
        style={{ ...bottomButton, width: "35%", marginLeft: "8%" }}
      />
      <MainColorButton
        label="受取薬履歴"
        onClick={() => navigate(MEDICAL_HISTORY)}
        style={{ ...bottomButton, width: "35%", marginLeft: "58%" }}
      />
    </>
  );
};

export default PrescriptionHistory;
